import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Checkbox, Loading, ErrorNote,
    confirmAction, toast
} from '../components/ui';
import { DataTable } from '../components/DataTable';

interface Template {
    id: number;
    name: string;
    description: string | null;
    position: string | null;
    durationMinutes: number;
    questionCount: number;
    defaultMaxAnswerSeconds: number;
    proctoringProfileId: number;
    passMark: string | number;
    maxScorePerAnswer: string | number;
    useAiGeneratedQuestions: boolean;
    isDefault: boolean;
    questionIds?: number[];
    criterionIds?: number[];
    proctoringProfile?: { id: number; name: string };
}

const EMPTY = {
    id: undefined as number | undefined,
    name: '', description: '', position: '',
    durationMinutes: '15', questionCount: '5', defaultMaxAnswerSeconds: '120',
    proctoringProfileId: '', passMark: '3', maxScorePerAnswer: '5',
    useAiGeneratedQuestions: false, isDefault: false,
    questionIds: [] as number[], criterionIds: [] as number[]
};

export function Templates() {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState<ApiError | null>(null);
    const queryClient = useQueryClient();

    const templates = useQuery({
        queryKey: ['templates'],
        queryFn: () => api.get<Template[]>('/api/admin/templates')
    });

    const profiles = useQuery({
        queryKey: ['proctoring'],
        queryFn: () => api.get<{ id: number; name: string }[]>('/api/admin/proctoring')
    });

    const questions = useQuery({
        queryKey: ['questions', ''],
        queryFn: () => api.get<{ id: number; questionText: string; criterion: { name: string } | null }[]>(
            '/api/admin/questions')
    });

    const criteria = useQuery({
        queryKey: ['criteria'],
        queryFn: () => api.get<{ id: number; name: string }[]>('/api/admin/criteria')
    });

    const save = useMutation({
        mutationFn: () => api.post('/api/admin/templates', {
            ...form,
            durationMinutes: Number(form.durationMinutes),
            questionCount: Number(form.questionCount),
            defaultMaxAnswerSeconds: Number(form.defaultMaxAnswerSeconds),
            proctoringProfileId: Number(form.proctoringProfileId),
            passMark: Number(form.passMark),
            maxScorePerAnswer: Number(form.maxScorePerAnswer)
        }),
        onSuccess: async () => {
            toast('Template saved.');
            setForm(EMPTY);
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['templates'] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/templates/${id}/delete`),
        onSuccess: async () => {
            toast('Interview format retired.');
            await queryClient.invalidateQueries({ queryKey: ['templates'] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    const load = async (id: number) => {
        const full = await api.get<Template>(`/api/admin/templates/${id}`);
        setForm({
            id: full.id,
            name: full.name,
            description: full.description ?? '',
            position: full.position ?? '',
            durationMinutes: String(full.durationMinutes),
            questionCount: String(full.questionCount),
            defaultMaxAnswerSeconds: String(full.defaultMaxAnswerSeconds),
            proctoringProfileId: String(full.proctoringProfileId),
            passMark: String(Number(full.passMark)),
            maxScorePerAnswer: String(Number(full.maxScorePerAnswer)),
            useAiGeneratedQuestions: full.useAiGeneratedQuestions,
            isDefault: full.isDefault,
            questionIds: full.questionIds ?? [],
            criterionIds: full.criterionIds ?? []
        });
    };

    const toggleId = (key: 'questionIds' | 'criterionIds', id: number) =>
        setForm((f) => ({
            ...f,
            [key]: f[key].includes(id) ? f[key].filter((v) => v !== id) : [...f[key], id]
        }));

    return (
        <div className="space-y-6">
            <Card heading title="Interview formats"
            lede="A format is what gets asked, for how long, and under which camera rules. Creating an interview means picking a candidate and a format.">
                {templates.isLoading ? <Loading /> : (
                    <DataTable
                        rows={templates.data ?? []}
                        rowKey={(row) => row.id}
                        empty="No templates yet."
                        columns={[
                            {
                                header: 'Name', sortBy: (row) => row.name,
                                cell: (row) => (
                                    <span className="font-medium">
                                        {row.name}
                                        {row.isDefault && (
                                            <span className="ml-2 text-xs font-normal text-slate-500">
                                                default
                                            </span>
                                        )}
                                    </span>
                                )
                            },
                            { header: 'Role', sortBy: (row) => row.position, cell: (row) => row.position ?? '—' },
                            { header: 'Questions', sortBy: (row) => row.questionCount, cell: (row) => row.questionCount },
                            { header: 'Minutes', sortBy: (row) => row.durationMinutes, cell: (row) => row.durationMinutes },
                            { header: 'Proctoring', sortBy: (row) => row.proctoringProfile?.name ?? null, cell: (row) => row.proctoringProfile?.name ?? '—' },
                            {
                                header: 'Questions from',
                                cell: (row) => row.useAiGeneratedQuestions ? 'Generated' : 'The bank'
                            },
                            {
                                header: '',
                                className: 'text-right',
                                cell: (row) => (
                                    <div className="flex justify-end gap-2">
                                        <Button variant="secondary" onClick={() => load(row.id)}>Edit</Button>
                                        <Button
                                            variant="secondary"
                                            onClick={async () => {
                                                const confirmed = await confirmAction({
                                                    title: `Remove ${row.name}?`,
                                                    confirmText: 'Retire format',
                                                    danger: true
                                                });
                                                if (confirmed) remove.mutate(row.id);
                                            }}
                                        >
                                            Retire
                                        </Button>
                                    </div>
                                )
                            }
                        ]}
                    />
                )}
            </Card>

            <Card title={form.id ? `Edit ${form.name}` : 'Add a template'}>
                <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
                    {error && !error.errors && <ErrorNote message={error.message} />}

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Field label="Name" error={error?.fieldError('name')}>
                            <TextInput
                                value={form.name}
                                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                invalid={!!error?.fieldError('name')}
                            />
                        </Field>

                        <Field label="Role this is for" hint="Fed to the model when generating questions.">
                            <TextInput
                                value={form.position}
                                onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                            />
                        </Field>
                    </div>

                    <Field label="Description">
                        <TextArea
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />
                    </Field>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <Field label="Length in minutes">
                            <TextInput
                                type="number" min="1"
                                value={form.durationMinutes}
                                onChange={(e) => setForm((f) => ({ ...f, durationMinutes: e.target.value }))}
                            />
                        </Field>

                        <Field label="Number of questions">
                            <TextInput
                                type="number" min="1"
                                value={form.questionCount}
                                onChange={(e) => setForm((f) => ({ ...f, questionCount: e.target.value }))}
                            />
                        </Field>

                        <Field label="Seconds per answer">
                            <TextInput
                                type="number" min="10"
                                value={form.defaultMaxAnswerSeconds}
                                onChange={(e) => setForm((f) => ({ ...f, defaultMaxAnswerSeconds: e.target.value }))}
                            />
                        </Field>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <Field label="Proctoring rules" error={error?.fieldError('proctoringProfileId')}>
                            <Select
                                value={form.proctoringProfileId}
                                onChange={(e) => setForm((f) => ({ ...f, proctoringProfileId: e.target.value }))}
                                invalid={!!error?.fieldError('proctoringProfileId')}
                            >
                                <option value="">Choose…</option>
                                {(profiles.data ?? []).map((p) => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </Select>
                        </Field>

                        <Field label="Top of the scale" hint="Highest score an answer can get.">
                            <TextInput
                                type="number" step="0.5" min="1"
                                value={form.maxScorePerAnswer}
                                onChange={(e) => setForm((f) => ({ ...f, maxScorePerAnswer: e.target.value }))}
                            />
                        </Field>

                        <Field label="Pass mark" hint="Advisory only. Nothing is auto-rejected.">
                            <TextInput
                                type="number" step="0.5" min="0"
                                value={form.passMark}
                                onChange={(e) => setForm((f) => ({ ...f, passMark: e.target.value }))}
                            />
                        </Field>
                    </div>

                    <div className="space-y-2.5">
                        <Checkbox
                            label="Generate questions with AI for each candidate"
                            hint="Needs an AI provider configured. Falls back to the bank if generation fails."
                            checked={form.useAiGeneratedQuestions}
                            onChange={(checked) =>
                                setForm((f) => ({ ...f, useAiGeneratedQuestions: checked }))}
                        />
                        <Checkbox
                            label="Use this template by default"
                            hint="Used when an integration creates an interview without naming one."
                            checked={form.isDefault}
                            onChange={(checked) => setForm((f) => ({ ...f, isDefault: checked }))}
                        />
                    </div>

                    {!form.useAiGeneratedQuestions && (
                        <Field
                            label="Questions to ask, in order"
                            error={error?.fieldError('questionIds')}
                        >
                            <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 p-3">
                                {(questions.data ?? []).map((q) => (
                                    <Checkbox
                                        key={q.id}
                                        label={q.questionText}
                                        hint={q.criterion?.name}
                                        checked={form.questionIds.includes(q.id)}
                                        onChange={() => toggleId('questionIds', q.id)}
                                    />
                                ))}
                            </div>
                        </Field>
                    )}

                    <Field
                        label="Score against these criteria"
                        hint="Leave all unticked to use every active criterion."
                    >
                        <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg border border-slate-200 p-3">
                            {(criteria.data ?? []).map((c) => (
                                <Checkbox
                                    key={c.id}
                                    label={c.name}
                                    checked={form.criterionIds.includes(c.id)}
                                    onChange={() => toggleId('criterionIds', c.id)}
                                />
                            ))}
                        </div>
                    </Field>

                    <div className="flex gap-2">
                        <Button type="submit" busy={save.isPending}>Save template</Button>
                        {form.id && (
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => { setForm(EMPTY); setError(null); }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </form>
            </Card>
        </div>
    );
}
